<template>
  <div class="prod-page">
    <ProductionPageHeader
      title="Team"
      subtitle="Invite people into Production. Only invited members can open this workspace."
    />

    <p v-if="!isProductionAdmin" class="prod-banner prod-banner--ready">
      <span class="material-icons">lock</span>
      Only production admins can invite teammates.
    </p>

    <form v-else class="move-card prod-panel space-y-5" @submit.prevent="invite">
      <ProductionSectionHead title="Invite someone" subtitle="They’ll get Move access so they can sign in and open Production." />
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="move-field !mb-0">
          <label for="invite-email">Email</label>
          <input id="invite-email" v-model="form.email" type="email" required class="move-input" placeholder="name@tiye.services" />
        </div>
        <div class="move-field !mb-0">
          <label for="invite-role">Role</label>
          <MoveSelect id="invite-role" v-model="form.role" block :options="workspaceRoleOptions" />
        </div>
        <div class="move-field !mb-0">
          <label for="invite-name">First name</label>
          <input id="invite-name" v-model="form.name" class="move-input" />
        </div>
        <div class="move-field !mb-0">
          <label for="invite-surname">Surname</label>
          <input id="invite-surname" v-model="form.surname" class="move-input" />
        </div>
      </div>
      <p v-if="formError" class="move-alert-error">{{ formError }}</p>
      <p v-if="formSuccess" class="prod-success">{{ formSuccess }}</p>
      <button type="submit" class="move-btn-primary" :disabled="saving">
        <span class="material-icons text-lg">person_add</span>
        {{ saving ? 'Inviting…' : 'Send invite' }}
      </button>
    </form>

    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading team" />
    </div>
    <div v-else-if="!members.length" class="move-card move-empty-state py-12">
      <span class="material-icons">group</span>
      <p class="font-semibold move-text">No one invited yet</p>
    </div>
    <div v-else class="space-y-2">
      <article
        v-for="member in members"
        :key="member.id"
        class="prod-person-row !p-4"
      >
        <span class="prod-person-avatar">{{ initials([member.name, member.surname].filter(Boolean).join(' ')) }}</span>
        <div class="min-w-0 flex-1">
          <p class="font-semibold move-text truncate">{{ member.name }} {{ member.surname }}</p>
          <p class="text-xs move-text-faint truncate">{{ member.email }}</p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 min-w-[9rem]">
          <MoveSelect
            :model-value="member.role"
            size="compact"
            :disabled="!isProductionAdmin"
            :options="workspaceRoleOptions"
            @update:model-value="updateMember(member, { role: $event })"
          />
          <MoveSelect
            :model-value="member.status"
            size="compact"
            :disabled="!isProductionAdmin"
            :options="memberStatusOptions"
            @update:model-value="updateMember(member, { status: $event })"
          />
        </div>
        <button
          v-if="isProductionAdmin"
          type="button"
          class="move-icon-btn move-icon-btn--danger"
          aria-label="Remove teammate"
          @click="askRemoveMember(member)"
        >
          <span class="material-icons">close</span>
        </button>
      </article>
    </div>

    <MoveConfirmDialog
      v-model="showRemoveModal"
      title="Remove teammate"
      subtitle="They will lose access to Production."
      :detail-title="memberToRemoveName"
      :detail-body="memberToRemove?.email || ''"
      detail-tone="danger"
      confirm-label="Remove"
      confirm-icon="person_remove"
      busy-label="Removing…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemoveMember"
      @cancel="closeRemoveModal"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import ProductionPageHeader from '../../components/production/ProductionPageHeader.vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'
import { memberStatusOptions, workspaceRoleOptions } from '../../config/productionNavigation'
import { initials } from '../../utils/productionDates'

const store = useStore()
const isProductionAdmin = computed(() => store.getters['auth/isProductionAdmin'])
const loading = ref(true)
const error = ref('')
const formError = ref('')
const formSuccess = ref('')
const saving = ref(false)
const members = ref([])
const form = reactive({ email: '', name: '', surname: '', role: 'member' })
const showRemoveModal = ref(false)
const memberToRemove = ref(null)
const removing = ref(false)
const removeError = ref('')
const memberToRemoveName = computed(() => {
  const member = memberToRemove.value
  if (!member) return ''
  const name = [member.name, member.surname].filter(Boolean).join(' ').trim()
  return name || member.email || 'this teammate'
})

async function load () {
  loading.value = true
  error.value = ''
  try {
    const body = await productionApi.listTeam()
    members.value = body.data || []
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load team.'
  } finally {
    loading.value = false
  }
}

async function invite () {
  saving.value = true
  formError.value = ''
  formSuccess.value = ''
  try {
    await productionApi.inviteTeam({ ...form })
    form.email = ''
    form.name = ''
    form.surname = ''
    formSuccess.value = 'Invite sent.'
    await load()
  } catch (e) {
    formError.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Invite failed.'
  } finally {
    saving.value = false
  }
}

async function updateMember (member, payload) {
  await productionApi.updateTeam(member.id, payload)
  await load()
}

function askRemoveMember (member) {
  memberToRemove.value = member
  removeError.value = ''
  showRemoveModal.value = true
}

function closeRemoveModal () {
  if (removing.value) return
  showRemoveModal.value = false
  memberToRemove.value = null
  removeError.value = ''
}

async function confirmRemoveMember () {
  const member = memberToRemove.value
  if (!member || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.removeTeam(member.id)
    showRemoveModal.value = false
    memberToRemove.value = null
    await load()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not remove this teammate.'
  } finally {
    removing.value = false
  }
}

onMounted(load)
</script>
